/**
 * Import.jsx - ERP data import page.
 *
 * Provides three import channels:
 *   1. Manual file upload with schema detection and column mapping preview
 *   2. Watched folder (server-side inbox) for automatic file pickup
 *   3. Live Dynamics 365 connection for direct data pull
 *
 * Also displays a chronological import history table and a column-name
 * guide modal listing every recognised field per entity.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Upload,
  FileSpreadsheet,
  FolderSync,
  Cloud,
  History,
  BookOpen,
  Play,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { Tile, DataTable, Badge, Modal, Loading, useToast } from "../components";

import {
  previewImport,
  runImport,
  getImportHistory,
  getWatchedFolderStatus,
  scanWatchedFolder,
  getDynamicsConnectionStatus,
  testDynamicsConnection,
  pullFromDynamics,
  getColumnGuide,
} from "../api/import";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const DATA_TYPES = [
  { value: "auto", label: "Auto-detect" },
  { value: "vendors", label: "Vendors" },
  { value: "products", label: "Products / Items" },
  { value: "orders", label: "Purchase Orders" },
  { value: "invoices", label: "Invoices" },
  { value: "receipts", label: "Receipts / Goods Received" },
  { value: "payments", label: "Payments" },
  { value: "quality", label: "Quality Inspections" },
];

/* ------------------------------------------------------------------ */
/* Column definitions                                                  */
/* ------------------------------------------------------------------ */

const MAPPING_COLUMNS = [
  { key: "fileColumn", label: "File Column" },
  { key: "mappedTo", label: "Maps To" },
  { key: "confidence", label: "Confidence", type: "pct" },
];

const UNMAPPED_COLUMNS = [
  { key: "fileColumn", label: "File Column" },
  { key: "sample", label: "Sample Value" },
];

const WAITING_FILES_COLUMNS = [
  { key: "name", label: "File Name" },
  { key: "size", label: "Size", type: "num" },
  { key: "detectedType", label: "Detected Type" },
  { key: "addedAt", label: "Added", type: "date" },
];

const HISTORY_COLUMNS = [
  { key: "when", label: "When", type: "date" },
  { key: "source", label: "Source" },
  { key: "dataType", label: "Data Type" },
  { key: "schema", label: "Schema" },
  { key: "rows", label: "Rows", type: "int" },
  { key: "newRows", label: "New", type: "int" },
  { key: "updated", label: "Updated", type: "int" },
  { key: "skipped", label: "Skipped", type: "int" },
  { key: "errors", label: "Errors", type: "int" },
  {
    key: "duration",
    label: "Duration",
    render: (v) => (v ? `${v.toFixed(1)}s` : "\u2014"),
  },
  { key: "importedBy", label: "By" },
  { key: "notes", label: "Notes" },
];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function ImportPage() {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  /* Upload state */
  const [file, setFile] = useState(null);
  const [dataType, setDataType] = useState("auto");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  /* Watched folder state */
  const [watchedFolder, setWatchedFolder] = useState(null);
  const [watchedLoading, setWatchedLoading] = useState(false);

  /* Dynamics connection state */
  const [dynamicsStatus, setDynamicsStatus] = useState(null);
  const [dynamicsLoading, setDynamicsLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [pulling, setPulling] = useState(false);

  /* Import history */
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  /* Column guide modal */
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideData, setGuideData] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);

  /* Full-page loading */
  const [initialLoading, setInitialLoading] = useState(true);

  /* ---------------------------------------------------------------- */
  /* Data fetching                                                     */
  /* ---------------------------------------------------------------- */

  const loadInitialData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const [folderRes, dynamicsRes, historyRes] = await Promise.all([
        getWatchedFolderStatus().catch(() => null),
        getDynamicsConnectionStatus().catch(() => null),
        getImportHistory().catch(() => []),
      ]);
      setWatchedFolder(folderRes);
      setDynamicsStatus(dynamicsRes);
      setHistory(historyRes);
    } catch (err) {
      console.error("Failed to load import page data:", err);
      toast("Failed to load import status", "bad");
    } finally {
      setInitialLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  /* ---------------------------------------------------------------- */
  /* File upload handlers                                              */
  /* ---------------------------------------------------------------- */

  const handleFileChange = useCallback((e) => {
    const selected = e.target.files?.[0];
    setFile(selected || null);
    setPreview(null);
  }, []);

  const handlePreviewMapping = useCallback(async () => {
    if (!file) {
      toast("Select a file first", "warn");
      return;
    }
    setPreviewLoading(true);
    try {
      const result = await previewImport(file, dataType);
      setPreview(result);
      toast("Mapping preview generated", "good");
    } catch (err) {
      console.error("Preview failed:", err);
      toast(err.message || "Preview failed", "bad");
    } finally {
      setPreviewLoading(false);
    }
  }, [file, dataType, toast]);

  const handleImport = useCallback(async () => {
    if (!file) {
      toast("Select a file first", "warn");
      return;
    }
    setImporting(true);
    try {
      const result = await runImport(file, dataType);
      toast(
        `Import complete: ${result.newRows ?? 0} new, ${result.updated ?? 0} updated, ${result.errors ?? 0} errors`,
        result.errors > 0 ? "warn" : "good",
      );
      // Reset upload area
      setFile(null);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      // Refresh history
      const historyRes = await getImportHistory().catch(() => []);
      setHistory(historyRes);
    } catch (err) {
      console.error("Import failed:", err);
      toast(err.message || "Import failed", "bad");
    } finally {
      setImporting(false);
    }
  }, [file, dataType, toast]);

  /* ---------------------------------------------------------------- */
  /* Watched folder handlers                                           */
  /* ---------------------------------------------------------------- */

  const handleScanFolder = useCallback(async () => {
    setWatchedLoading(true);
    try {
      const result = await scanWatchedFolder();
      setWatchedFolder(result);
      toast(
        `Scan complete: ${result.processed ?? 0} files processed`,
        "good",
      );
      // Refresh history after folder scan
      const historyRes = await getImportHistory().catch(() => []);
      setHistory(historyRes);
    } catch (err) {
      console.error("Folder scan failed:", err);
      toast(err.message || "Folder scan failed", "bad");
    } finally {
      setWatchedLoading(false);
    }
  }, [toast]);

  /* ---------------------------------------------------------------- */
  /* Dynamics 365 handlers                                             */
  /* ---------------------------------------------------------------- */

  const handleTestConnection = useCallback(async () => {
    setTestingConnection(true);
    try {
      const result = await testDynamicsConnection();
      setDynamicsStatus((prev) => ({
        ...prev,
        connected: result.success,
        lastTested: new Date().toISOString(),
        lastError: result.success ? null : result.error,
      }));
      toast(
        result.success ? "Connection successful" : "Connection failed",
        result.success ? "good" : "bad",
      );
    } catch (err) {
      console.error("Connection test failed:", err);
      toast(err.message || "Connection test failed", "bad");
    } finally {
      setTestingConnection(false);
    }
  }, [toast]);

  const handlePullFromDynamics = useCallback(async () => {
    setPulling(true);
    try {
      const result = await pullFromDynamics();
      toast(
        `Pulled ${result.rows ?? 0} rows from Dynamics 365`,
        "good",
      );
      // Refresh history
      const historyRes = await getImportHistory().catch(() => []);
      setHistory(historyRes);
    } catch (err) {
      console.error("Dynamics pull failed:", err);
      toast(err.message || "Pull from Dynamics failed", "bad");
    } finally {
      setPulling(false);
    }
  }, [toast]);

  /* ---------------------------------------------------------------- */
  /* Column guide                                                      */
  /* ---------------------------------------------------------------- */

  const handleOpenGuide = useCallback(async () => {
    setGuideOpen(true);
    if (guideData) return; // Already loaded
    setGuideLoading(true);
    try {
      const data = await getColumnGuide();
      setGuideData(data);
    } catch (err) {
      console.error("Failed to load column guide:", err);
      toast("Failed to load column guide", "bad");
    } finally {
      setGuideLoading(false);
    }
  }, [guideData, toast]);

  /* ---------------------------------------------------------------- */
  /* Render helpers                                                    */
  /* ---------------------------------------------------------------- */

  function renderPreviewSection() {
    if (!preview) return null;

    const { entity, confidence, rowCount, mapping, unmapped } = preview;

    return (
      <div style={s.previewBox}>
        <div style={s.previewHeader}>
          <div style={s.previewMeta}>
            <div style={s.previewMetaItem}>
              <span style={s.previewLabel}>Detected Entity</span>
              <strong>{entity || "Unknown"}</strong>
            </div>
            <div style={s.previewMetaItem}>
              <span style={s.previewLabel}>Confidence</span>
              <strong>
                {confidence != null ? `${(confidence * 100).toFixed(0)}%` : "\u2014"}
              </strong>
            </div>
            <div style={s.previewMetaItem}>
              <span style={s.previewLabel}>Rows Detected</span>
              <strong>{rowCount ?? "\u2014"}</strong>
            </div>
          </div>
        </div>

        {/* Column mapping table */}
        {mapping && mapping.length > 0 && (
          <div style={s.previewSubSection}>
            <h4 style={s.previewSubTitle}>Column Mapping</h4>
            <DataTable columns={MAPPING_COLUMNS} rows={mapping} />
          </div>
        )}

        {/* Unmapped fields */}
        {unmapped && unmapped.length > 0 && (
          <div style={s.previewSubSection}>
            <h4 style={s.previewSubTitle}>
              <AlertTriangle
                size={14}
                style={{ color: "var(--warn)", verticalAlign: "text-bottom" }}
              />{" "}
              Unmapped Fields ({unmapped.length})
            </h4>
            <DataTable columns={UNMAPPED_COLUMNS} rows={unmapped} />
          </div>
        )}
      </div>
    );
  }

  function renderWatchedFolderCard() {
    const folder = watchedFolder || {};
    const waitingFiles = folder.waitingFiles || [];

    return (
      <div className="card">
        <div className="card-head">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FolderSync size={18} style={{ color: "var(--accent)" }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              Watched Folder
            </h3>
          </div>
          <button
            className="btn"
            onClick={handleScanFolder}
            disabled={watchedLoading}
          >
            {watchedLoading ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Play size={14} />
            )}
            Scan Now
          </button>
        </div>
        <div className="card-body">
          <div style={s.infoRow}>
            <span style={s.infoLabel}>Inbox Path</span>
            <code style={s.infoValue}>{folder.inboxPath || "\u2014"}</code>
          </div>
          <div style={s.infoRow}>
            <span style={s.infoLabel}>Automatic Watching</span>
            <Badge
              text={folder.watching ? "Active" : "Disabled"}
              variant={folder.watching ? "good" : "warn"}
            />
          </div>

          {waitingFiles.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={s.previewSubTitle}>
                Waiting Files ({waitingFiles.length})
              </h4>
              <DataTable
                columns={WAITING_FILES_COLUMNS}
                rows={waitingFiles}
                emptyText="No files waiting"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderDynamicsCard() {
    const status = dynamicsStatus || {};

    return (
      <div className="card">
        <div className="card-head">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Cloud size={18} style={{ color: "var(--accent)" }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              Live Connection to Dynamics 365
            </h3>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn ghost"
              onClick={handleTestConnection}
              disabled={testingConnection}
            >
              {testingConnection ? (
                <Loader2 size={14} className="spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              Test Connection
            </button>
            <button
              className="btn"
              onClick={handlePullFromDynamics}
              disabled={pulling || !status.enabled}
            >
              {pulling ? (
                <Loader2 size={14} className="spin" />
              ) : (
                <Cloud size={14} />
              )}
              Pull Now
            </button>
          </div>
        </div>
        <div className="card-body">
          <div style={s.infoRow}>
            <span style={s.infoLabel}>Status</span>
            <Badge
              text={status.enabled ? "Enabled" : "Disabled"}
              variant={status.enabled ? "good" : "bad"}
            />
          </div>
          {status.connected != null && (
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Connection</span>
              {status.connected ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--good)" }}>
                  <CheckCircle2 size={14} /> Connected
                </span>
              ) : (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--bad)" }}>
                  <XCircle size={14} /> Disconnected
                </span>
              )}
            </div>
          )}
          {status.lastTested && (
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Last Tested</span>
              <span>
                {new Date(status.lastTested).toLocaleString("en-PH", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          )}
          {status.lastError && (
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Last Error</span>
              <span style={{ color: "var(--bad)" }}>{status.lastError}</span>
            </div>
          )}
          {status.endpoint && (
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Endpoint</span>
              <code style={s.infoValue}>{status.endpoint}</code>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderColumnGuideModal() {
    if (!guideOpen) return null;

    return (
      <Modal
        title="Column Name Guide"
        onClose={() => setGuideOpen(false)}
        footer={
          <button className="btn ghost" onClick={() => setGuideOpen(false)}>
            Close
          </button>
        }
      >
        {guideLoading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Loader2 size={24} className="spin" style={{ color: "var(--accent)" }} />
            <p style={{ marginTop: 12, color: "var(--ink-3)", fontSize: 13 }}>
              Loading column guide...
            </p>
          </div>
        ) : guideData ? (
          <div>
            <p style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 16 }}>
              The following column names are recognised for each entity type.
              Use these names (or close variants) in your import files for
              automatic mapping.
            </p>
            {Object.entries(guideData).map(([entity, columns]) => (
              <div key={entity} style={{ marginBottom: 18 }}>
                <h4
                  style={{
                    fontSize: 13,
                    fontWeight: 650,
                    textTransform: "capitalize",
                    marginBottom: 8,
                    color: "var(--accent)",
                  }}
                >
                  {entity}
                </h4>
                <div style={s.guideTags}>
                  {(Array.isArray(columns) ? columns : []).map((col) => (
                    <span key={col} style={s.guideTag}>
                      {col}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: "var(--ink-3)" }}>
            Unable to load column guide. Please try again.
          </p>
        )}
      </Modal>
    );
  }

  /* ---------------------------------------------------------------- */
  /* Main render                                                       */
  /* ---------------------------------------------------------------- */

  return (
    <>
      <Loading show={initialLoading} text="Loading import status..." />

      {/* Upload export file card */}
      <div className="card">
        <div className="card-head">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Upload size={18} style={{ color: "var(--accent)" }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              Upload Export File
            </h3>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn ghost"
              onClick={handlePreviewMapping}
              disabled={!file || previewLoading}
            >
              {previewLoading ? (
                <Loader2 size={14} className="spin" />
              ) : (
                <FileSpreadsheet size={14} />
              )}
              Preview Mapping
            </button>
            <button
              className="btn"
              onClick={handleImport}
              disabled={!file || importing}
            >
              {importing ? (
                <Loader2 size={14} className="spin" />
              ) : (
                <ArrowRight size={14} />
              )}
              Import
            </button>
          </div>
        </div>
        <div className="card-body">
          <div style={s.uploadRow}>
            <div style={s.uploadField}>
              <label style={s.fieldLabel} htmlFor="import-file">
                File
              </label>
              <input
                id="import-file"
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.json,.xml"
                onChange={handleFileChange}
                style={s.fileInput}
              />
              {file && (
                <span style={s.fileName}>{file.name}</span>
              )}
            </div>
            <div style={s.uploadField}>
              <label style={s.fieldLabel} htmlFor="import-type">
                Data Type
              </label>
              <select
                id="import-type"
                value={dataType}
                onChange={(e) => setDataType(e.target.value)}
                style={s.select}
              >
                {DATA_TYPES.map((dt) => (
                  <option key={dt.value} value={dt.value}>
                    {dt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview section */}
          {renderPreviewSection()}
        </div>
      </div>

      {/* Two-column layout for watched folder + dynamics */}
      <div style={s.twoCol}>
        {renderWatchedFolderCard()}
        {renderDynamicsCard()}
      </div>

      {/* Import history */}
      <div className="card">
        <div className="card-head">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <History size={18} style={{ color: "var(--accent)" }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              Import History
            </h3>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn ghost"
              onClick={handleOpenGuide}
            >
              <BookOpen size={14} />
              Column Name Guide
            </button>
            <button
              className="btn ghost"
              onClick={async () => {
                setHistoryLoading(true);
                try {
                  const res = await getImportHistory().catch(() => []);
                  setHistory(res);
                } finally {
                  setHistoryLoading(false);
                }
              }}
              disabled={historyLoading}
            >
              {historyLoading ? (
                <Loader2 size={14} className="spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              Refresh
            </button>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <DataTable
            columns={HISTORY_COLUMNS}
            rows={history}
            emptyText="No imports yet"
          />
        </div>
      </div>

      {/* Column guide modal */}
      {renderColumnGuideModal()}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const s = {
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
    marginBottom: 20,
  },
  uploadRow: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  uploadField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    flex: "1 1 200px",
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--ink-2)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  fileInput: {
    fontSize: 13,
    color: "var(--ink)",
    padding: "6px 0",
  },
  select: {
    fontSize: 13,
    padding: "9px 12px",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--line)",
    background: "var(--panel)",
    color: "var(--ink)",
    outline: "none",
  },
  fileName: {
    fontSize: 12,
    color: "var(--accent)",
    fontWeight: 550,
    marginTop: 4,
  },
  previewBox: {
    marginTop: 16,
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
    overflow: "hidden",
  },
  previewHeader: {
    padding: "12px 16px",
    background: "var(--bg)",
    borderBottom: "1px solid var(--line-2)",
  },
  previewMeta: {
    display: "flex",
    gap: 24,
    flexWrap: "wrap",
  },
  previewMetaItem: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 13,
  },
  previewLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "var(--ink-3)",
  },
  previewSubSection: {
    padding: "12px 16px",
    borderBottom: "1px solid var(--line-2)",
  },
  previewSubTitle: {
    fontSize: 13,
    fontWeight: 650,
    margin: "0 0 8px 0",
    color: "var(--ink-2)",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "6px 0",
    fontSize: 13,
  },
  infoLabel: {
    fontWeight: 600,
    color: "var(--ink-2)",
    minWidth: 140,
    flexShrink: 0,
  },
  infoValue: {
    fontSize: 12,
    background: "var(--bg)",
    padding: "2px 8px",
    borderRadius: "var(--radius-sm)",
    wordBreak: "break-all",
  },
  guideTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  guideTag: {
    display: "inline-block",
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 550,
    fontFamily: "monospace",
    background: "var(--bg)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    color: "var(--ink-2)",
  },
};
